import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MagnifyingGlass, CircleNotch, DownloadSimple, MusicNotesSimple,
  CheckCircle, Clock, ArrowLeft, WhatsappLogo, VideoCamera,
} from "@phosphor-icons/react";
import { API, whatsappLink } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import axios from "axios";

export default function OrderTracking() {
  const navigate = useNavigate();
  const { t } = useLang();
  const STAGE_ORDER = ["pedido_realizado", "escribiendo_letra", "letra_entregada", "revision_letra", "letra_aprobada", "creando_melodia", "cancion_entregada"];
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloading2, setDownloading2] = useState(false);
  const [downloadingKaraoke, setDownloadingKaraoke] = useState(false);
  const [order, setOrder] = useState(null);
  const [searchParams] = useSearchParams();

  const runSearch = async (raw) => {
    const value = (raw || "").trim().toUpperCase();
    if (!value) return;
    setLoading(true);
    setOrder(null);
    try {
      const { data } = await axios.get(`${API}/track/${value}`);
      setOrder(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("track.notFound"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const c = searchParams.get("code");
    if (c) {
      setCode(c.toUpperCase());
      runSearch(c);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const search = (e) => {
    e?.preventDefault();
    runSearch(code);
  };

  const downloadMp3 = async (suffix, labelName) => {
    const setter = suffix === "-2" ? setDownloading2 : setDownloading;
    setter(true);
    try {
      const res = await axios.get(`${API}/track/${order.tracking_code}/download${suffix}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${labelName}-${order.tracking_code}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t("track.dlError"));
    } finally {
      setter(false);
    }
  };
  const download = () => downloadMp3("", "cancion");
  const download2 = () => downloadMp3("-2", "cancion-2");

  const downloadKaraoke = async () => {
    setDownloadingKaraoke(true);
    try {
      const res = await axios.get(`${API}/track/${order.tracking_code}/download-karaoke`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `karaoke-${order.tracking_code}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t("track.dlErrorKaraoke"));
    } finally {
      setDownloadingKaraoke(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="blob w-[420px] h-[420px] -top-24 -left-24" style={{ background: "hsl(351 100% 82%)" }} />
      <div className="blob w-[360px] h-[360px] bottom-0 right-0" style={{ background: "hsl(200 80% 82%)" }} />

      <div className="relative max-w-xl mx-auto px-5 pt-16 pb-24">
        <button data-testid="track-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-bold text-foreground/50 hover:text-primary mb-8">
          <ArrowLeft size={18} /> {t("track.back")}
        </button>

        <div className="text-center mb-8">
          <img src="/logo.png" alt="logo" className="h-16 mx-auto mb-5" />
          <h1 className="font-display font-bold text-4xl tracking-tight text-foreground">{t("track.title")}</h1>
          <p className="text-foreground/60 font-medium mt-2">{t("track.subtitle")}</p>
        </div>

        <form onSubmit={search} className="rounded-3xl bg-white shadow-soft border border-white p-3 flex gap-2 mb-8">
          <input
            data-testid="track-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Ej. A1B2C3"
            className="flex-1 rounded-2xl bg-transparent px-4 font-bold text-lg tracking-widest uppercase outline-none"
          />
          <motion.button
            data-testid="track-search-btn"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            disabled={loading}
            className="flex items-center gap-2 rounded-2xl bg-primary px-6 py-3.5 font-bold text-primary-foreground shadow-soft disabled:opacity-60"
          >
            {loading ? <CircleNotch className="animate-spin" size={20} /> : <MagnifyingGlass size={20} />}
            <span className="hidden sm:block">{t("track.search")}</span>
          </motion.button>
        </form>

        {order && (
          <motion.div
            data-testid="track-result"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-[2rem] bg-white shadow-soft-lg border border-white p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider">{t("track.code")}</p>
                <p className="font-display font-bold text-2xl text-foreground tracking-widest">{order.tracking_code}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider">{t("track.client")}</p>
                <p className="font-bold text-foreground">{order.contact_name}</p>
              </div>
            </div>

            <div className="rounded-2xl bg-muted p-4 mb-6 flex items-center justify-between">
              <span className="font-bold text-foreground/70 text-sm">{order.package_name}</span>
              <span className="text-foreground/50 text-sm font-semibold">{order.delivery_name}</span>
            </div>

            <div className="mb-8">
              <p className="text-xs font-bold text-foreground/40 uppercase tracking-wider mb-4">{t("track.progressTitle")}</p>
              <div className="space-y-0">
                {STAGE_ORDER.map((sk, idx) => {
                  const currentIdx = STAGE_ORDER.indexOf(order.stage || "pedido_realizado");
                  const done = idx < currentIdx;
                  const active = idx === currentIdx;
                  const isLast = idx === STAGE_ORDER.length - 1;
                  return (
                    <div key={sk} data-testid={`stage-${sk}`} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${done ? "bg-green-500 text-white" : active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/30"}`}>
                          {done ? <CheckCircle weight="fill" size={18} /> : active ? <Clock weight="fill" size={16} /> : <span className="text-xs font-bold">{idx + 1}</span>}
                        </div>
                        {!isLast && <div className={`w-0.5 flex-1 min-h-[22px] ${idx < currentIdx ? "bg-green-500" : "bg-muted"}`} />}
                      </div>
                      <div className={`pb-4 pt-0.5 ${active ? "" : "opacity-70"}`}>
                        <p className={`text-sm font-bold ${active ? "text-primary" : done ? "text-foreground" : "text-foreground/50"}`}>
                          {t(`track.stages.${sk}`)}
                        </p>
                        {active && <p className="text-xs font-semibold text-foreground/40">{t("track.currentStage")}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {order.mp3_available || order.karaoke_available ? (
              <div className="space-y-3">
                {order.mp3_available && (
                  <motion.button
                    data-testid="track-download-btn"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={download}
                    disabled={downloading}
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-foreground text-background py-4 font-bold shadow-soft disabled:opacity-60"
                  >
                    {downloading ? <CircleNotch className="animate-spin" size={20} /> : <DownloadSimple weight="bold" size={20} />}
                    {order.mp3_multiple ? t("track.download1") : t("track.download")}
                  </motion.button>
                )}
                {order.mp3_multiple && order.mp3_available_2 && (
                  <motion.button
                    data-testid="track-download-2-btn"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={download2}
                    disabled={downloading2}
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-foreground text-background py-4 font-bold shadow-soft disabled:opacity-60"
                  >
                    {downloading2 ? <CircleNotch className="animate-spin" size={20} /> : <DownloadSimple weight="bold" size={20} />}
                    {t("track.download2")}
                  </motion.button>
                )}
                {order.karaoke_ordered && order.karaoke_available && (
                  <motion.button
                    data-testid="track-download-karaoke-btn"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={downloadKaraoke}
                    disabled={downloadingKaraoke}
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-secondary text-secondary-foreground py-4 font-bold shadow-soft disabled:opacity-60"
                  >
                    {downloadingKaraoke ? <CircleNotch className="animate-spin" size={20} /> : <VideoCamera weight="bold" size={20} />}
                    {t("track.downloadKaraoke")}
                  </motion.button>
                )}
                {order.karaoke_ordered && !order.karaoke_available && (
                  <p className="text-center text-sm font-semibold text-foreground/50">{t("track.karaokePending")}</p>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border-2 border-dashed border-border p-6 text-center">
                <MusicNotesSimple weight="duotone" size={36} className="text-primary mx-auto mb-2" />
                <p className="font-bold text-foreground/70">{t("track.inProduction")}</p>
                <p className="text-sm text-foreground/50 mt-1">{t("track.comeBack")}</p>
                <a href={whatsappLink(`Hola, quiero dar seguimiento a mi pedido con código ${order.tracking_code}`)} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#25D366]">
                  <WhatsappLogo weight="fill" size={16} /> {t("track.contact")}
                </a>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
